import React, {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import gsap from 'gsap';
import useWindowSize from '@/hooks/useWindowSize';
import styles from 'components/blocks/header-rotating-shape/HeaderRotatingShape.module.scss';

const BACKGROUND_ICON_VIEWBOX_SIZE = 15000;
const BACKGROUND_ICON_CENTER = BACKGROUND_ICON_VIEWBOX_SIZE / 2;
const DEFAULT_ROTATION_DURATION = 180;

type BackgroundPinwheelProps = {
  iconColor?: string;
  isVisible?: boolean;
  disableRevealOnVisibilityChange?: boolean;
  position?: Pick<React.CSSProperties, 'top' | 'right' | 'bottom' | 'left'>;
  revealDelay?: number;
  ref?: React.Ref<BackgroundPinwheelRef>;
};

export type BackgroundPinwheelRef = {
  elementRef: React.RefObject<SVGSVGElement | null>;
  revealTimelineRef: React.RefObject<gsap.core.Timeline>;
  setRotationDuration: (duration: number) => void;
};

const defaultPosition: NonNullable<BackgroundPinwheelProps['position']> = {
  top: '60%',
  left: '80%',
};

export default function BackgroundPinwheel({
  iconColor = '#fff',
  isVisible,
  disableRevealOnVisibilityChange = false,
  position = defaultPosition,
  revealDelay = 1.5,
  ref,
}: BackgroundPinwheelProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const backgroundIconMaskId = useId();
  const bgIconRef = useRef<SVGGElement>(null);
  const bgIconMaskRef = useRef<SVGRectElement>(null);
  const revealTimelineRef = useRef<gsap.core.Timeline>(gsap.timeline({ paused: true }));
  const bgRectRef = useRef<SVGRectElement>(null);
  const rotationTweenRef = useRef<gsap.core.Tween>(null);
  const rotationSpeedTweenRef = useRef<gsap.core.Tween>(null);

  const { width: windowWidth, height: windowHeight } = useWindowSize();

  const rectangleWidth = 2 * windowWidth;
  const rectangleHeight = (100 * windowHeight) / 75;

  useLayoutEffect(() => {
    if (!bgIconMaskRef.current || !bgRectRef.current || !bgIconRef.current) return undefined;

    const revealTimeline = revealTimelineRef.current;
    revealTimeline.clear();
    revealTimeline
      .fromTo(
        [bgRectRef.current, bgIconRef.current],
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.1,
          ease: 'power2.in',
        },
        0,
      )
      .fromTo(
        bgIconMaskRef.current,
        {
          attr: {
            rx: '50%',
            ry: '50%',
          },
          '--mask-scale': 0,
        },
        {
          attr: {
            rx: 0,
            ry: 0,
          },
          '--mask-scale': 1,
          duration: 0.8,
          ease: 'power2.inOut',
        },
        revealDelay,
      )
      .pause(0);

    return () => {
      revealTimeline.clear();
    };
  }, [revealDelay, windowWidth, windowHeight]);

  useEffect(() => {
    if (!bgIconRef.current) return undefined;

    gsap.set(bgIconRef.current, { rotation: -25, transformOrigin: '50% 50%' });
    rotationTweenRef.current = gsap.to(bgIconRef.current, {
      rotation: '+=360',
      duration: DEFAULT_ROTATION_DURATION,
      ease: 'none',
      repeat: -1,
      transformOrigin: '50% 50%',
    });

    return () => {
      rotationSpeedTweenRef.current?.kill();
      rotationSpeedTweenRef.current = null;
      rotationTweenRef.current?.kill();
      rotationTweenRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (typeof isVisible !== 'boolean') return undefined;

    if (isVisible) {
      revealTimelineRef.current?.play();
    } else {
      revealTimelineRef.current?.reverse();
    }
    return undefined;
  }, [isVisible]);

  const setRotationDuration = useCallback((duration: number) => {
    if (!rotationTweenRef.current || duration <= 0) return;

    rotationSpeedTweenRef.current?.kill();
    rotationSpeedTweenRef.current = gsap.to(rotationTweenRef.current, {
      timeScale: DEFAULT_ROTATION_DURATION / duration,
      duration: 0.4,
      ease: 'power2.out',
      overwrite: true,
    });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      elementRef: svgRef,
      revealTimelineRef,
      setRotationDuration,
    }),
    [setRotationDuration],
  );

  return (
    <svg
      width={BACKGROUND_ICON_VIEWBOX_SIZE}
      height={BACKGROUND_ICON_VIEWBOX_SIZE}
      viewBox={`0 0 ${BACKGROUND_ICON_VIEWBOX_SIZE} ${BACKGROUND_ICON_VIEWBOX_SIZE}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.backgroundIcon}
      style={position}
      ref={svgRef}
    >
      <defs>
        <clipPath id={backgroundIconMaskId} clipPathUnits="userSpaceOnUse">
          <rect
            ref={bgIconMaskRef}
            className={styles.backgroundIconMask}
            style={{ '--mask-scale': 0 } as React.CSSProperties}
            width={rectangleWidth}
            height={rectangleHeight}
            x={BACKGROUND_ICON_CENTER - rectangleWidth / 2}
            y={BACKGROUND_ICON_CENTER - rectangleHeight / 2}
          />
        </clipPath>
      </defs>
      <g clipPath={`url(#${backgroundIconMaskId})`}>
        <g ref={bgIconRef} className={styles.backgroundIconSpin}>
          <rect
            width={BACKGROUND_ICON_VIEWBOX_SIZE}
            height={BACKGROUND_ICON_VIEWBOX_SIZE}
            fill={iconColor}
            ref={bgRectRef}
          />
          <path
            d="M7500.02 7499.99C9215.75 9215.72 9215.75 11997.5 7500.02 13713.2C5784.28 15428.9 3002.53 15428.9 1286.79 13713.2"
            fill="var(--background-color)"
          />
          <path
            d="M7500.02 7500.01C5784.28 5784.28 5784.28 3002.53 7500.02 1286.8C9215.75 -428.933 11997.5 -428.933 13713.2 1286.8"
            fill="var(--background-color)"
          />
          <path
            d="M7500.03 7500C5784.29 9215.73 3002.54 9215.73 1286.8 7500C-428.934 5784.27 -428.933 3002.52 1286.8 1286.79"
            fill="var(--background-color)"
          />
          <path
            d="M7500.01 7500C9215.74 5784.27 11997.5 5784.27 13713.2 7500C15429 9215.73 15429 11997.5 13713.2 13713.2"
            fill="var(--background-color)"
          />
        </g>
      </g>
    </svg>
  );
}
